import classNames from 'classnames';
import React, {ReactNode} from 'react';
import _ from 'underscore';

import * as Analytics from 'analytics/Analytics';
import * as benchlingUtil from 'BenchlingUtil';
import {Tag} from 'folder-items/FileConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import {CheckSampleStatusParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import BenchLink from 'link-preview/BenchLink';
import TagValueViewer from 'metadata/TagValueViewer';
import {ContainerContentTablesQuery} from 'samples/__generated/ContainerContentTablesQuery';
import getTestProps from 'test/lib/cypress/getTestProps';
import CollapserChevron from 'ui-library/CollapserChevron';

import ConfirmableMeasurementField from './ConfirmableMeasurementField';
import {MeasurementType} from './MeasurementForm';
import * as StorableActions from './StorableActions';

const NUM_COLS = 4;

export type ContainerContentNode = ExtractByTypename<
  ContainerContentTablesQuery['containers'][number],
  'ContainerNode'
>['contents'][number];

interface DetailCellProps {
  colSpan: number;
  name: ReactNode;
  value?: ReactNode;
}

export function DetailCell({name, value, colSpan}: DetailCellProps): JSX.Element {
  return (
    <td className="contentDetail" colSpan={colSpan}>
      <div className="contentDetail-name">{name}</div>
      {value ? <div className="u-ellipsis-text">{value}</div> : <div className="u-faded">None</div>}
    </td>
  );
}
DetailCell.defaultProps = {
  colSpan: 1,
};

interface NameDetailCellProps {
  id: string;
  name: ReactNode;
  url: string | null;
}

/**
 * Render name of the contained item, with link to open it
 */
function NameDetailCell(props: NameDetailCellProps): JSX.Element {
  const value =
    props.url != null ? (
      <BenchLink href={benchlingUtil.pathToURL(props.url)}>{props.name}</BenchLink>
    ) : (
      props.name
    );
  return <DetailCell name="Name" key="contentName" value={value} />;
}

export interface ContainerContentDetailsTableProps {
  containerId: string;
  containerContent: ContainerContentNode;
  name: ReactNode;
  residues?: string | null;
  showResidues?: boolean;
  tags: Array<Tag>;
  url: string | null;
  checkSampleStatus: (params: CheckSampleStatusParams) => Promise<boolean>;
  disableEditsWithTooltip?: string | null;
  isWritable?: boolean;
}

interface ContainerContentDetailsTableState {
  isExpanded: boolean;
}

interface Detail {
  key: string;
  name: string;
  value: ReactNode;
}

export default class ContainerContentDetailsTable extends React.Component<
  ContainerContentDetailsTableProps,
  ContainerContentDetailsTableState
> {
  state: ContainerContentDetailsTableState = {
    isExpanded: true,
  };

  private getDetailRows = (details: Array<Detail>): Array<JSX.Element> => {
    const numRows = Math.ceil(Number(details.length) / NUM_COLS);
    // eslint-disable-next-line no-restricted-syntax
    return _.times(numRows, (i) => (
      <tr className="gridView-contentDetailsRow" key={i}>
        {/* eslint-disable-next-line no-restricted-syntax */}
        {_.times(NUM_COLS, (j) => {
          const index = i * NUM_COLS + j;
          if (index >= details.length) {
            return null;
          }
          return (
            <DetailCell key={details[index].key} name={details[index].name} value={details[index].value} />
          );
        })}
      </tr>
    ));
  };

  private toggleExpanded = (): void => {
    this.setState({isExpanded: !this.state.isExpanded});
  };

  render(): JSX.Element {
    const details: Array<Detail> = [
      {
        key: 'sampleLotNumber',
        name: 'Sample aliquot number',
        value: this.props.containerContent.lotNumber,
      },
      ...(this.props.showResidues
        ? [
            {
              key: 'residues',
              name: 'Sequence',
              value: <span className="u-ellipsis-text selectable mono">{this.props.residues}</span>,
            },
          ]
        : []),
      ...this.props.tags.map((tag) => ({
        key: tag.name,
        name: tag.name,
        value: (
          <TagValueViewer tag={tag} shouldPreviewImages={false} summarize={true} summarizeThreshold={3} />
        ),
      })),
    ];
    return (
      <div
        className={classNames('gridView-contentDetails', {
          'gridView-contentDetails--expanded': this.state.isExpanded,
        })}
        {...getTestProps({component: 'ContainerContentDetailsTable', element: 'root'})}
      >
        <table className="gridView-contentDetailsTable">
          <tbody>
            <tr className="gridView-contentDetailsRow">
              <NameDetailCell
                id={getAccessibleOrInaccessibleId(this.props.containerContent.containedItem)}
                name={this.props.name}
                url={this.props.url}
              />
              <DetailCell
                colSpan={3}
                name="Concentration"
                value={
                  <ConfirmableMeasurementField
                    measurement={this.props.containerContent.concentrationJson}
                    onComplete={(concentration) => {
                      StorableActions.updateContainerContentWithDataMods(
                        this.props.containerId,
                        getAccessibleOrInaccessibleId(this.props.containerContent.containedItem),
                        concentration
                      );
                    }}
                    isDisabled={!this.props.isWritable || this.props.disableEditsWithTooltip != null}
                    measurementType={MeasurementType.CONCENTRATION}
                    confirmBeforeStartEditing={() =>
                      this.props.checkSampleStatus({
                        actionAttempted: 'update_concentration',
                        eventGroupId: Analytics.makeAnalyticsEventId(),
                      })
                    }
                    disableEditsWithTooltip={this.props.disableEditsWithTooltip}
                    dataTestAttrs={{component: 'ContainerContentDetailsTable', element: 'concentration'}}
                  />
                }
              />
              <td className="contentDetail-iconCell">
                <CollapserChevron
                  isExpanded={this.state.isExpanded}
                  name="contents"
                  onToggleCollapsed={this.toggleExpanded}
                />
              </td>
            </tr>
            {this.state.isExpanded && this.getDetailRows(details)}
          </tbody>
        </table>
      </div>
    );
  }
}
